import { Copy, Edit, StarIcon } from 'lucide-react';
import { twMerge } from 'tailwind-merge';

import { Accordion, AccordionContent, AccordionItem, AccordionTrigger, Button, FilledStar } from '@/_components';
import type { PhoneNumberQuerySchema } from '@/_schemas/phone-numbers';
import { snackbar, useCopyToClipboard } from '@/_shared';

import { PhoneNumberLinkerPreview } from './phone-numbers-linker-preview';

import { DeleteButton } from '../../buttons/delete-button';

type PhoneNumbersLinkerProps = {
  phone_numbers: PhoneNumberQuerySchema[] | undefined | null;
  removeItemHandler: () => void;
  editItemHandler?: () => void;
  isEditable: boolean;
  itemClassName?: string;
  containerClassName?: string;
  showPrimaryContactAction?: boolean;
  showPrimaryTeamMemberAction?: boolean; // Added this
  isPrimary?: boolean;
  hasPrimary?: boolean;
  setContactType?: VoidFunction;
  setTeamMemberType?: VoidFunction; // Added this
  phoneNumberClassName?: string;
  phoneNumberContainerClassName?: string;
};

export function PhoneNumbersLinker({
  phone_numbers,
  removeItemHandler,
  editItemHandler,
  isEditable,
  itemClassName,
  containerClassName,
  showPrimaryContactAction = false,
  showPrimaryTeamMemberAction = false, // Added this
  isPrimary = false,
  hasPrimary = false,
  setContactType,
  setTeamMemberType, // Added this
  phoneNumberClassName,
  phoneNumberContainerClassName,
}: PhoneNumbersLinkerProps) {
  const { copy } = useCopyToClipboard();

  const [first, ...rest] = phone_numbers || [];
  const hasMore = (rest ?? []).length > 0;

  const renderPrimaryAction = () => {
    if (showPrimaryContactAction && (isEditable || isPrimary)) {
      return (
        <Button
          icon={isPrimary ? FilledStar : StarIcon}
          tag="div"
          as="link"
          size="sm"
          aria-label="Contact Type"
          iconClassName="text-gray-400"
          className={!isPrimary ? 'opacity-40 hover:opacity-100' : undefined}
          disabled={!isEditable}
          onClick={isEditable ? setContactType : undefined}
        />
      );
    }
    if (showPrimaryContactAction && hasPrimary) {
      return <div className="size-7 shrink-0" />;
    }
    return null;
  };

  const renderTeamMemberAction = () => {
    if (showPrimaryTeamMemberAction && (isEditable || isPrimary)) {
      return (
        <Button
          icon={isPrimary ? FilledStar : StarIcon}
          tag="div"
          as="link"
          size="sm"
          aria-label="Team Member Type"
          iconClassName="text-gray-400"
          className={!isPrimary ? 'opacity-40 hover:opacity-100' : undefined}
          disabled={!isEditable}
          onClick={isEditable ? () => setTeamMemberType : undefined} // Using setTeamMemberType
        />
      );
    }
    if (showPrimaryTeamMemberAction && hasPrimary) {
      return <div className="size-7 shrink-0" />;
    }
    return null;
  };

  const actions = () => (
    <>
      {first && (
        <Button
          aria-label="Copy number"
          size="sm"
          tag="div"
          as="link"
          icon={Copy}
          className="opacity-40 hover:opacity-100"
          onClick={(e) => {
            e.stopPropagation();
            copy(first.number);
            snackbar.success(`${first.number} has been copied to clipboard`);
          }}
        />
      )}
      {showPrimaryContactAction && renderPrimaryAction()}
      {showPrimaryTeamMemberAction && renderTeamMemberAction()} {/* Render the team member action */}
      {isEditable && (
        <>
          {typeof editItemHandler === 'function' && (
            <Button
              tag="div"
              size="sm"
              as="link"
              aria-label="Edit Contact"
              className="shrink-0 opacity-40 hover:opacity-100"
              icon={Edit}
              onClick={editItemHandler}
            />
          )}
          <DeleteButton
            type="icon"
            size="sm"
            title="Remove Contact?"
            text="The Contact record will not be deleted; only its association will be removed."
            yesButton="Yes, Remove"
            aria-label="Remove Contact"
            callback={removeItemHandler}
          />
        </>
      )}
    </>
  );

  return (
    <article className={twMerge('w-full items-start py-0.5', containerClassName)}>
      <Accordion type="single" collapsible className="w-full">
        <AccordionItem value="item-1">
          <AccordionTrigger
            invisibleIcon={!hasMore}
            className={twMerge('flex w-full justify-end gap-x-1.5', itemClassName)}
            actions={actions()}
          >
            {first && (
              <PhoneNumberLinkerPreview
                phoneNumber={first}
                className={phoneNumberClassName}
                containerClassName={phoneNumberContainerClassName}
              />
            )}
          </AccordionTrigger>

          {hasMore && (
            <AccordionContent>
              {rest.map((phoneNumber) =>
                phoneNumber.country ? (
                  <div
                    className={twMerge(
                      'mt-3 flex w-full flex-1 items-center justify-end gap-x-1.5 font-medium',
                      itemClassName
                    )}
                    key={phoneNumber.id}
                  >
                    <PhoneNumberLinkerPreview phoneNumber={phoneNumber} />
                    <article className="flex items-center gap-1.5">
                      <Button
                        aria-label="Copy number"
                        size="sm"
                        tag="div"
                        as="link"
                        icon={Copy}
                        className="opacity-40 hover:opacity-100"
                        onClick={() => {
                          copy(phoneNumber.number);
                          snackbar.success(`${phoneNumber.number} has been copied to clipboard`);
                        }}
                      />
                      {renderPrimaryAction()}
                      {renderTeamMemberAction()} {/* Render the team member action for other phone numbers */}
                      {isEditable && typeof editItemHandler === 'function' && <div className="size-7 shrink-0" />}
                      {isEditable && <div className="size-7 shrink-0" />}
                      <div className="size-4 shrink-0" />
                    </article>
                  </div>
                ) : null
              )}
            </AccordionContent>
          )}
        </AccordionItem>
      </Accordion>
    </article>
  );
}
